import { isGuestAddress, normalizeWalletAddress, type WalletAddress } from "./wallet";

/*
 * Paying for someone, and saying where winnings go. The USDG rooms take an entry from any wallet that pays it: a
 * player's own wallet, or another wallet that pays for them (a guest's). The vault takes the payment
 * from the wallet that sends it (the payer), refunds it there, and pays a round's pot to the winner's address.
 *
 * - A payer proof: the paying wallet signs that it pays this player's entries (free, it moves no money). Without one,
 *   anyone could name someone else's wallet as theirs and hold it out of a round (the vault takes one payment per
 *   wallet per round).
 * - A payout claim: a guest has no wallet of its own, so when it wins, the wallet that paid its entry signs where
 *   the pot goes (any EVM address). Until it does (for a while), the pot waits; then it goes to that wallet. A claim
 *   names the guest and the round: it covers that one win only (a wallet that paid for several guests, or for one
 *   guest's several rounds, can't have a claim used for another win).
 *
 * Both are plain text messages, signed with personal_sign (EIP-191). The server checks every line. A player's name in
 * them is the way the game shows it (a nickname, Guest-1234, or a shortened address): plain letters, digits, spaces
 * and _ . - … only, so nothing in it can pass for another line, another address or hide what follows it in a wallet's
 * window (no line breaks, look-alike brackets, right-to-left marks or invisible characters).
 */

/** a payer proof is valid at most this long (it's made just before paying) */
export const PAYER_PROOF_MAX_MS = 15 * 60_000;
/** a payout claim is taken this long after it was signed (made just before it's sent) */
export const PAYOUT_CLAIM_MAX_AGE_MS = 10 * 60_000;
/** the clocks of a phone and the server may differ this much */
export const CLOCK_SKEW_MS = 2 * 60_000;

const PROOF_TITLE = "TRENCHIO: this wallet pays a player's entries";
const CLAIM_TITLE = "TRENCHIO: where my winnings go";
const ADDRESS = /^0x[0-9a-fA-F]{40}$/u;
/** a site name as it shows in a browser's address bar (a host, maybe with a port) */
const SITE = /^[a-z0-9.-]{1,253}(?::\d{1,5})?$/u;
/** a round's ID (the game makes UUIDs) */
const ROUND = /^[A-Za-z0-9_-]{1,80}$/u;
/** a player's name in a message: the "Player" line, as the game writes it (see cleanName) */
const PLAYER_LINE = /^([A-Za-z0-9 _.…-]{1,40}) \((0x[0-9a-fA-F]{40})\)$/u;

export interface PayerProofFields {
	/** the site it was signed for (the address bar's host) */
	readonly site: string;
	/** the player the entries are for (a guest's made-up address, or a wallet) */
	readonly player: WalletAddress;
	/** how the player is called (shown to the one signing) */
	readonly playerName: string;
	/** the wallet that pays */
	readonly payer: WalletAddress;
	/** ms since 1970: after this the proof is no longer taken */
	readonly validUntil: number;
}

/** what the paying wallet signs (shown in the wallet as it is) */
export function payerProofMessage(fields: PayerProofFields): string {
	return [
		PROOF_TITLE,
		"",
		`Player: ${cleanName(fields.playerName)} (${fields.player.toLowerCase()})`,
		`Wallet that pays: ${fields.payer.toLowerCase()}`,
		`Site: ${fields.site.toLowerCase()}`,
		`Valid until: ${new Date(fields.validUntil).toISOString()}`,
		"",
		"Signing is free and moves no money: each entry is still paid by its own transaction, which this wallet confirms.",
	].join("\n");
}

export interface PayoutClaimFields {
	readonly site: string;
	/** the guest that won (its made-up address), and its name (shown to the one signing) */
	readonly player: WalletAddress;
	readonly playerName: string;
	/** the round it won: the claim is for that win only */
	readonly roundId: string;
	/** where the winnings go */
	readonly payTo: WalletAddress;
	/** the wallet that paid the winning entry (it signs) */
	readonly payer: WalletAddress;
	/** ms since 1970 */
	readonly signedAt: number;
}

/** what the wallet that paid a guest's entry signs to say where its winnings from one round go */
export function payoutClaimMessage(fields: PayoutClaimFields): string {
	if (!ROUND.test(fields.roundId)) throw new TypeError("The round's ID is invalid");
	return [
		CLAIM_TITLE,
		"",
		`Player: ${cleanName(fields.playerName)} (${fields.player.toLowerCase()})`,
		`Round: ${fields.roundId}`,
		`Send my winnings to: ${fields.payTo.toLowerCase()}`,
		`Wallet that paid my entry: ${fields.payer.toLowerCase()}`,
		`Site: ${fields.site.toLowerCase()}`,
		`Signed at: ${new Date(fields.signedAt).toISOString()}`,
		"",
		"This sends this player's winnings from this round (paid with this wallet's entry) to the address above. Signing is free and moves no money.",
	].join("\n");
}

/**
 * a name as it may stand in a message: the characters the game's names are made of (a nickname's letters, digits,
 * spaces and _ . -; a shortened address's \u2026), one space at most between words, none at either end, at most 40
 * characters: so it reads back the same, and nothing in it can look like another line or an address of its own
 */
const cleanName = (name: string): string =>
	name
		.replace(/[^A-Za-z0-9 _.\u2026-]/gu, "")
		.replace(/ {2,}/gu, " ")
		.trim()
		.slice(0, 40)
		.trim() || "Player";

/** the one line starting with this label, its value (exactly one such line, or the message is refused) */
const field = (lines: readonly string[], label: string): string => {
	const found = lines.filter((line) => line.startsWith(`${label}: `));
	if (found.length !== 1) throw new TypeError(`The message's "${label}" line is missing`);
	return found[0].slice(label.length + 2);
};

const isoTime = (text: string, label: string): number => {
	if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/u.test(text)) throw new TypeError(`The message's "${label}" time is invalid`);
	const time = Date.parse(text);
	if (!Number.isFinite(time)) throw new TypeError(`The message's "${label}" time is invalid`);
	return time;
};

const address = (text: string, label: string): WalletAddress => {
	if (!ADDRESS.test(text)) throw new TypeError(`The message's "${label}" address is invalid`);
	return normalizeWalletAddress(text);
};

const site = (text: string): string => {
	if (!SITE.test(text)) throw new TypeError("The message's site is invalid");
	return text;
};

/** reads a payer proof message back: the very text payerProofMessage makes, or it's refused */
export function parsePayerProofMessage(message: string): PayerProofFields {
	if (typeof message !== "string" || message.length > 1_000) throw new TypeError("The payer message is invalid");
	const lines = message.split("\n");
	if (lines[0] !== PROOF_TITLE) throw new TypeError("This isn't a TRENCHIO payer message");
	const player = PLAYER_LINE.exec(field(lines, "Player"));
	if (!player) throw new TypeError(`The message's "Player" line is invalid`);
	const fields: PayerProofFields = {
		player: address(player[2], "Player"),
		playerName: player[1],
		payer: address(field(lines, "Wallet that pays"), "Wallet that pays"),
		site: site(field(lines, "Site")),
		validUntil: isoTime(field(lines, "Valid until"), "Valid until"),
	};
	// exactly the text the game asks to sign: nothing added, nothing left out
	if (payerProofMessage(fields) !== message) throw new TypeError("The payer message isn't the one the game asks to sign");
	return fields;
}

/** reads a payout claim back: the very text payoutClaimMessage makes, or it's refused */
export function parsePayoutClaimMessage(message: string): PayoutClaimFields {
	if (typeof message !== "string" || message.length > 1_000) throw new TypeError("The payout message is invalid");
	const lines = message.split("\n");
	if (lines[0] !== CLAIM_TITLE) throw new TypeError("This isn't a TRENCHIO payout message");
	const player = PLAYER_LINE.exec(field(lines, "Player"));
	if (!player) throw new TypeError(`The message's "Player" line is invalid`);
	const roundId = field(lines, "Round");
	if (!ROUND.test(roundId)) throw new TypeError(`The message's "Round" line is invalid`);
	const fields: PayoutClaimFields = {
		player: address(player[2], "Player"),
		playerName: player[1],
		roundId,
		payTo: address(field(lines, "Send my winnings to"), "Send my winnings to"),
		payer: address(field(lines, "Wallet that paid my entry"), "Wallet that paid my entry"),
		site: site(field(lines, "Site")),
		signedAt: isoTime(field(lines, "Signed at"), "Signed at"),
	};
	if (payoutClaimMessage(fields) !== message) throw new TypeError("The payout message isn't the one the game asks to sign");
	return fields;
}

export interface SignedMessageV1 {
	readonly message: string;
	readonly signature: `0x${string}`;
}

/** a payer proof as sent with a payment request: the paying wallet, its signed message */
export interface PayerProofV1 extends SignedMessageV1 {
	readonly payer: WalletAddress;
}

/**
 * a signature as a wallet hands it back: 65 bytes for an ordinary wallet (or its 64-byte short form, EIP-2098), more
 * for a smart wallet's (bounded)
 */
const SIGNATURE = /^0x(?:[0-9a-fA-F]{128}|[0-9a-fA-F]{130,16000})$/u;

export function parseSignedMessage(value: unknown): SignedMessageV1 {
	if (typeof value !== "object" || value === null) throw new TypeError("The signed message is missing");
	const input = value as Record<string, unknown>;
	if (typeof input.message !== "string" || !input.message || input.message.length > 1_000)
		throw new TypeError("The signed message is invalid");
	if (typeof input.signature !== "string" || !SIGNATURE.test(input.signature) || input.signature.length % 2 !== 0)
		throw new TypeError("The signature is invalid");
	return { message: input.message, signature: input.signature as `0x${string}` };
}

export function parsePayerProof(value: unknown): PayerProofV1 {
	const signed = parseSignedMessage(value);
	const payer = (value as Record<string, unknown>).payer;
	if (typeof payer !== "string" || !ADDRESS.test(payer)) throw new TypeError("The paying wallet's address is invalid");
	const normalized = normalizeWalletAddress(payer);
	// the guests' own range of addresses: nobody's wallet (a key made to match it would be made on purpose)
	if (isGuestAddress(normalized)) throw new TypeError("This wallet can't pay here");
	return { ...signed, payer: normalized };
}

/** an address winnings can be sent to: a real EVM address (not a guest's made-up one, not the zero address) */
export function parsePayoutAddress(value: unknown): WalletAddress {
	if (typeof value !== "string" || !ADDRESS.test(value.trim())) throw new TypeError("Enter an EVM address: 0x followed by 40 letters and digits");
	const normalized = normalizeWalletAddress(value);
	if (isGuestAddress(normalized)) throw new TypeError("That's a guest's made-up address: nobody can receive anything there");
	if (/^0x0{40}$/u.test(normalized)) throw new TypeError("That's the zero address: nobody can receive anything there");
	return normalized;
}
